/**
 * Currency utilities — converts item prices to the visitor's preferred
 * currency using server-cached USD-based exchange rates.
 *
 * Display currency is auto-detected from the visitor's country via IP
 * geolocation (https://api.country.is — free, no key). Falls back to
 * the browser locale region, then USD.
 *
 * Detection result is cached in localStorage with a 7-day TTL so the
 * IP lookup happens at most once per week per browser.
 */
import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

const LS_KEY = "sc:displayCurrencyV2";
const TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

// Minimal country (ISO-2) → ISO-4217 currency map covering the visitor pool
// we actually expect. Anything not in here falls back to USD.
const COUNTRY_TO_CURRENCY = {
  US: "USD",
  CA: "CAD",
  GB: "GBP",
  AU: "AUD", NZ: "NZD",
  JP: "JPY", CN: "CNY", KR: "KRW", HK: "HKD", SG: "SGD", IN: "INR",
  BR: "BRL", MX: "MXN",
  CH: "CHF", NO: "NOK", SE: "SEK", DK: "DKK",
  PL: "PLN", CZ: "CZK", TR: "TRY", IL: "ILS", ZA: "ZAR",
  // Eurozone
  AT: "EUR", BE: "EUR", BG: "EUR", CY: "EUR", DE: "EUR", EE: "EUR", ES: "EUR",
  FI: "EUR", FR: "EUR", GR: "EUR", HR: "EUR", IE: "EUR", IT: "EUR", LT: "EUR",
  LU: "EUR", LV: "EUR", MT: "EUR", NL: "EUR", PT: "EUR", RO: "EUR", SI: "EUR",
  SK: "EUR",
};

function readCache() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.currency || !parsed?.ts) return null;
    if (Date.now() - parsed.ts > TTL_MS) return null;
    return parsed.currency;
  } catch { return null; }
}

function writeCache(currency) {
  try { localStorage.setItem(LS_KEY, JSON.stringify({ currency, ts: Date.now() })); }
  catch { /* ignore */ }
}

function detectFromBrowserLocale() {
  try {
    const candidates = [
      ...(navigator.languages || []),
      navigator.language || "",
    ];
    for (const l of candidates) {
      const region = l.split("-")[1]?.toUpperCase();
      if (region && COUNTRY_TO_CURRENCY[region]) return COUNTRY_TO_CURRENCY[region];
    }
  } catch { /* ignore */ }
  return null;
}

async function detectFromIp() {
  try {
    const r = await fetch("https://api.country.is", { cache: "no-store" });
    if (!r.ok) return null;
    const j = await r.json();
    const cc = (j?.country || "").toUpperCase();
    return COUNTRY_TO_CURRENCY[cc] || null;
  } catch {
    return null;
  }
}

/** React hook → returns the resolved display currency (string, e.g. "EUR").
 *  Strategy: cache → IP (authoritative) → browser locale → USD. */
export function useDisplayCurrency() {
  // Initialize from cache (instant) so the UI doesn't flash with USD then jump.
  const [cur, setCur] = useState(() => readCache() || "USD");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Skip if we already have a fresh cache entry — country rarely changes.
      if (readCache()) return;
      // 1) Try IP (most accurate)
      const ipCur = await detectFromIp();
      if (!cancelled && ipCur) {
        setCur(ipCur);
        writeCache(ipCur);
        return;
      }
      // 2) Fall back to browser locale
      const localeCur = detectFromBrowserLocale();
      if (!cancelled && localeCur) {
        setCur(localeCur);
        writeCache(localeCur);
        return;
      }
      // 3) Last-resort USD (don't cache so we'll retry next visit)
      if (!cancelled) setCur("USD");
    })();
    return () => { cancelled = true; };
  }, []);

  return cur;
}

/** React hook → fetches & caches the USD-based exchange-rate table from our API. */
export function useExchangeRates() {
  return useQuery({
    queryKey: ["public-exchange-rates"],
    queryFn: async () => (await api.get("/public/exchange-rates")).data,
    staleTime: 1000 * 60 * 60 * 6, // 6h client-side cache (server already caches 12h)
    refetchOnWindowFocus: false,
  });
}

/** Convert ``amount`` from ``fromCcy`` to ``toCcy`` using USD-based rates.
 *  Returns ``null`` if conversion isn't possible (missing rate, no amount). */
export function convertAmount(amount, fromCcy, toCcy, rates) {
  if (amount === null || amount === undefined || isNaN(amount)) return null;
  if (!rates || !rates.rates) return null;
  const from = (fromCcy || "USD").toUpperCase();
  const to = (toCcy || "USD").toUpperCase();
  if (from === to) return Number(amount);
  const fromRate = from === "USD" ? 1 : rates.rates[from];
  const toRate = to === "USD" ? 1 : rates.rates[to];
  if (!fromRate || !toRate) return null;
  const usd = Number(amount) / fromRate;
  return usd * toRate;
}

/** Format a number as ``~$84`` style (whole number + tilde prefix). */
export function formatConverted(amount, ccy) {
  if (amount === null || amount === undefined || isNaN(amount)) return null;
  const rounded = Math.round(Number(amount));
  try {
    const s = new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: (ccy || "USD").toUpperCase(),
      maximumFractionDigits: 0,
    }).format(rounded);
    return `~${s}`;
  } catch {
    return `~${rounded} ${ccy}`;
  }
}

/** Convenience helper used by cards & detail pages. Returns a string like ``~€84``
 *  ready for rendering, or ``null`` if conversion isn't possible. */
export function useConvertedPrice(price, fromCurrency) {
  const target = useDisplayCurrency();
  const { data: rates } = useExchangeRates();
  if (price === null || price === undefined) return null;
  const value = convertAmount(price, fromCurrency, target, rates);
  return formatConverted(value, target);
}
